import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Building2, Upload, Trash2, Eye, Save, Mail, MapPin, FileText, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BrandingSettings() {
  const { token, selectedCondominium } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoPerso, setLogoPerso] = useState(false);

  // Il logo si legge con la rotta autenticata e si mostra come blob (un <img> non puo' mandare il token)
  const caricaLogoBlob = async () => {
    try {
      const r = await fetch(`${API_URL}/api/settings/branding/logo-file`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { setLogoUrl(null); return false; }
      setLogoUrl(URL.createObjectURL(await r.blob()));
      return true;
    } catch (e) { setLogoUrl(null); return false; }
  };
  const [workspaceName, setWorkspaceName] = useState('');
  
  const [formData, setFormData] = useState({
    studio_name: '',
    admin_name: '',
    address: '',
    city: '',
    postal_code: '',
    province: '',
    country: 'Italia',
    phone: '',
    email: '',
    pec: '',
    vat_number: '',
    fiscal_code: '',
    footer_text: '',
    website: '',
    primary_color: '#1e3a5f',
    secondary_color: '#64748b',
  });

  const fetchBranding = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/settings/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setWorkspaceName(data.workspace_name || '');
        if (data.logo_url) { await caricaLogoBlob(); } else { setLogoUrl(null); }
        // c'e' un logo registrato ma il file non e' piu' sul disco: va ricaricato (ricevute e documenti uscirebbero senza)
        setLogoPerso(Boolean(data.branding?.logo_document_id) && data.logo_su_disco === false);
        
        if (data.branding) {
          setFormData(prev => ({
            ...prev,
            ...data.branding
          }));
        }
      } else {
        toast.error('Errore nel caricamento delle impostazioni');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/settings/branding`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('Impostazioni salvate con successo');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo file non supportato. Usa PNG, JPEG, SVG o WebP');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Il file non può superare 2MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/settings/branding/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        await caricaLogoBlob();
        setLogoPerso(false);
        toast.success('Logo caricato con successo');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nel caricamento');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!confirm('Rimuovere il logo?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/settings/branding/logo`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setLogoUrl(null);
        toast.success('Logo rimosso');
      } else {
        toast.error('Errore nella rimozione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await fetch(`${API_URL}/api/settings/branding/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        toast.error('Errore nella generazione anteprima');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <PageHeader
        title="Branding"
        subtitle="Logo e intestazione personalizzati"
        actions={
          <>
            <Button variant="outline" className="bg-white text-primary hover:bg-white/90" onClick={handlePreview} disabled={previewing}>
              {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              Anteprima PDF
            </Button>
            <Button className="bg-white text-primary hover:bg-white/90" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salva
            </Button>
          </>
        }
      />

      {/* Logo Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Logo Aziendale
          </CardTitle>
          <CardDescription>
            Carica il logo dello studio (PNG, JPEG, SVG - max 2MB)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {logoPerso && (
              <div className="text-sm rounded-md border border-red-300 bg-red-50 text-red-800 p-3 max-w-xs">
                Il file del logo non è più presente sul server: ricaricalo, altrimenti ricevute e documenti escono senza logo.
              </div>
            )}
            {/* Logo Preview */}
            <div className="w-40 h-20 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center bg-gray-50">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="Logo" 
                  className="max-w-full max-h-full object-contain"
                  onError={() => setLogoUrl(null)}
                />
              ) : (
                <span className="text-gray-400 text-sm">Nessun logo</span>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex gap-2">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button variant="outline" asChild disabled={uploading}>
                  <span>
                    {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    Carica Logo
                  </span>
                </Button>
              </label>
              {logoUrl && (
                <Button variant="ghost" size="icon" onClick={handleLogoDelete}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Studio Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Informazioni Studio
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nome Studio/Azienda</Label>
              <Input
                value={formData.studio_name}
                onChange={(e) => handleInputChange('studio_name', e.target.value)}
                placeholder={workspaceName || 'Es: Studio Rossi Amministrazioni'}
              />
            </div>
            <div className="space-y-2">
              <Label>Nome Amministratore</Label>
              <Input
                value={formData.admin_name}
                onChange={(e) => handleInputChange('admin_name', e.target.value)}
                placeholder="Es: Dott. Mario Rossi"
              />
            </div>
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>P.IVA</Label>
              <Input
                value={formData.vat_number}
                onChange={(e) => handleInputChange('vat_number', e.target.value)}
                placeholder="IT12345678901"
              />
            </div>
            <div className="space-y-2">
              <Label>Codice Fiscale</Label>
              <Input
                value={formData.fiscal_code}
                onChange={(e) => handleInputChange('fiscal_code', e.target.value)}
                placeholder="RSSMRA80A01H501A"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Indirizzo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Indirizzo</Label>
            <Input
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder="Via Roma 1"
            />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2 col-span-2 md:col-span-1">
              <Label>CAP</Label>
              <Input
                value={formData.postal_code}
                onChange={(e) => handleInputChange('postal_code', e.target.value)}
                placeholder="00100"
              />
            </div>
            <div className="space-y-2 col-span-2 md:col-span-2">
              <Label>Città</Label>
              <Input
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="Roma"
              />
            </div>
            <div className="space-y-2">
              <Label>Prov.</Label>
              <Input
                value={formData.province}
                onChange={(e) => handleInputChange('province', e.target.value)}
                placeholder="RM"
                maxLength={2}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Contatti
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Telefono</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+39 06 1234567"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="info@studio.it"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>PEC</Label>
              <Input
                type="email"
                value={formData.pec}
                onChange={(e) => handleInputChange('pec', e.target.value)}
                placeholder="studio@pec.it"
              />
            </div>
            <div className="space-y-2">
              <Label>Sito Web</Label>
              <Input
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="www.studio.it"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer & Customization */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Piè di Pagina
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Testo Piè di Pagina</Label>
            <Textarea
              value={formData.footer_text}
              onChange={(e) => handleInputChange('footer_text', e.target.value)}
              placeholder="Testo personalizzato per il piè di pagina dei documenti..."
              rows={2}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Colore Primario</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) => handleInputChange('primary_color', e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={formData.primary_color}
                  onChange={(e) => handleInputChange('primary_color', e.target.value)}
                  className="font-mono"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Colore Secondario</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={formData.secondary_color}
                  onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={formData.secondary_color}
                  onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                  className="font-mono"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Live Preview */}
      <Card className="border-2 border-dashed">
        <CardHeader>
          <CardTitle>Anteprima Carta Intestata</CardTitle>
        </CardHeader>
        <CardContent>
          <div 
            className="bg-white border rounded-lg p-6 space-y-4"
            style={{ borderTopColor: formData.primary_color, borderTopWidth: '4px' }}
          >
            {/* Header Preview */}
            <div className="flex justify-between items-start">
              <div>
                {logoUrl && (
                  <img 
                    src={logoUrl} 
                    alt="Logo" 
                    className="h-12 mb-2 object-contain"
                    onError={() => {}}
                  />
                )}
                <h2 
                  className="text-lg font-bold"
                  style={{ color: formData.primary_color }}
                >
                  {formData.studio_name || workspaceName || 'Nome Studio'}
                </h2>
                {formData.admin_name && (
                  <p className="text-sm" style={{ color: formData.secondary_color }}>
                    {formData.admin_name}
                  </p>
                )}
              </div>
              <div className="text-right text-sm" style={{ color: formData.secondary_color }}>
                {formData.address && <p>{formData.address}</p>}
                {(formData.postal_code || formData.city) && (
                  <p>{formData.postal_code} {formData.city} {formData.province && `(${formData.province})`}</p>
                )}
                {formData.phone && <p>Tel: {formData.phone}</p>}
                {formData.email && <p>{formData.email}</p>}
              </div>
            </div>
            
            <hr style={{ borderColor: formData.primary_color }} />
            
            {/* Content placeholder */}
            <div className="py-8 text-center text-gray-400">
              [Contenuto documento]
            </div>
            
            {/* Footer Preview */}
            <hr className="border-gray-200" />
            <p className="text-xs text-center" style={{ color: formData.secondary_color }}>
              {formData.footer_text || 'Documento generato da Artego Condo Manager'}
              {formData.website && ` | ${formData.website}`}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
